export interface LeadFilters {
  // Date ranges
  entryDateFrom?: string; // YYYY-MM-DD
  entryDateTo?: string;
  closureDateFrom?: string;
  closureDateTo?: string;
  lastContactFrom?: string;
  lastContactTo?: string;

  // Multi-select
  stages?: string[];
  priorities?: string[];
  assignedTo?: string[];

  // Value range
  dealValueMin?: number;
  dealValueMax?: number;

  // Risk
  riskLevel?: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface FilterPreset {
  id: string;
  name: string;
  filters: LeadFilters;
  createdAt: string;
}

export interface DateRangePreset {
  label: string;
  getValue: () => { from: Date; to: Date };
}

// Helper function to get start of day
const getStartOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper function to get end of day
const getEndOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Helper function to get start of week (Monday)
const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper function to get end of week (Sunday)
const getEndOfWeek = (date: Date): Date => {
  const d = getStartOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Helper function to get start of month
const getStartOfMonth = (date: Date): Date => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper function to get end of month
const getEndOfMonth = (date: Date): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Entry Date Presets (when lead was created)
export const ENTRY_DATE_PRESETS: DateRangePreset[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfDay(today), to: getEndOfDay(today) };
    }
  },
  {
    label: 'This Week',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfWeek(today), to: getEndOfWeek(today) };
    }
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfMonth(today), to: getEndOfMonth(today) };
    }
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return {
        from: getStartOfMonth(lastMonth),
        to: getEndOfMonth(lastMonth)
      };
    }
  },
  {
    label: 'Last 3 Months',
    getValue: () => {
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return {
        from: getStartOfDay(threeMonthsAgo),
        to: getEndOfDay(today)
      };
    }
  }
];

// Closure Expected Date Presets (when deal is expected to close)
export const CLOSURE_DATE_PRESETS: DateRangePreset[] = [
  {
    label: 'This Week',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfWeek(today), to: getEndOfWeek(today) };
    }
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfMonth(today), to: getEndOfMonth(today) };
    }
  },
  {
    label: 'Next Month',
    getValue: () => {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return {
        from: getStartOfMonth(nextMonth),
        to: getEndOfMonth(nextMonth)
      };
    }
  },
  {
    label: 'Next Quarter',
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const nextQuarterStart = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 1);
      const nextQuarterEnd = new Date(nextQuarterStart);
      nextQuarterEnd.setMonth(nextQuarterEnd.getMonth() + 3);
      nextQuarterEnd.setDate(0); // Last day of the quarter
      return {
        from: getStartOfDay(nextQuarterStart),
        to: getEndOfDay(nextQuarterEnd)
      };
    }
  }
];

// Last Contact Date Presets (when lead was last contacted)
export const LAST_CONTACT_PRESETS: DateRangePreset[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date();
      return { from: getStartOfDay(today), to: getEndOfDay(today) };
    }
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return {
        from: getStartOfDay(sevenDaysAgo),
        to: getEndOfDay(today)
      };
    }
  },
  {
    label: 'Last 14 Days',
    getValue: () => {
      const today = new Date();
      const fourteenDaysAgo = new Date(today);
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      return {
        from: getStartOfDay(fourteenDaysAgo),
        to: getEndOfDay(today)
      };
    }
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return {
        from: getStartOfDay(thirtyDaysAgo),
        to: getEndOfDay(today)
      };
    }
  }
];

// Format date to YYYY-MM-DD for input fields
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse YYYY-MM-DD to Date
export const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};
